<template>
    <VueFinalModal
        v-model="isOpen"
        class="vfm-modal vfm-modal--revolut"
        content-class="vfm-modal-content vfm-modal-content--revolut"
        content-transition="vfm-fade"
        overlay-transition="vfm-fade"
        :click-to-close="false"
        :esc-to-close="false"
    >
        <!-- En-tête -->
        <header class="revolut-header">
            <h1 class="revolut-header__title">Paiement TPE Revolut</h1>
            <span class="revolut-header__amount">{{ formattedAmount }}</span>
        </header>

        <!-- Étape : sélection du TPE -->
        <section v-if="step === 'selecting'" class="revolut-body">
            <p class="revolut-instruction">Choisissez un terminal de paiement&nbsp;:</p>
            <div class="revolut-terminal-list">
                <div
                    v-for="terminal in terminalStore.availableTerminals"
                    :key="terminal._id"
                    class="card revolut-terminal-card"
                    :class="{ active: selectedTerminalId === terminal._id }"
                    @click="selectedTerminalId = terminal._id"
                >
                    <h3>{{ terminal.label }}</h3>
                    <span class="revolut-terminal-card__serial">{{ terminal.serialNumber }}</span>
                </div>
            </div>
        </section>

        <!-- Étape : en attente / traitement -->
        <section v-else-if="step === 'pending' || step === 'processing'" class="revolut-body revolut-body--centered">
            <div class="revolut-loader-ctn">
                <span class="obloader"></span>
            </div>
            <p class="revolut-status revolut-status--big">
                {{ step === 'processing' ? 'Paiement en cours…' : 'Présentez la carte au terminal' }}
            </p>
            <p class="revolut-status" v-if="activeTerminal">
                Terminal&nbsp;: <strong>{{ activeTerminal.label }}</strong>
            </p>
            <p class="revolut-timer" :class="{ 'revolut-timer--warn': elapsed > 90 }">
                <template v-if="elapsed > 90">⚠ {{ elapsed }}s — Vérifiez l'état du terminal</template>
                <template v-else>{{ elapsed }}s</template>
            </p>
        </section>

        <!-- Timeout dépassé -->
        <section v-else-if="step === 'timeout'" class="revolut-body revolut-body--centered">
            <p class="revolut-error">Plus de réponse du terminal.<br>Veuillez vérifier l'état du TPE.</p>
        </section>

        <!-- Échec -->
        <section v-else-if="step === 'failed'" class="revolut-body revolut-body--centered">
            <div class="revolut-error-icon">
                <IconSvg name="close" width="36" />
            </div>
            <p class="revolut-error">{{ errorMessage || 'Le paiement a échoué.' }}</p>
        </section>

        <!-- Annulé -->
        <section v-else-if="step === 'cancelled'" class="revolut-body revolut-body--centered">
            <p class="revolut-error">Paiement annulé.</p>
        </section>

        <!-- Footer : actions selon l'étape -->
        <footer class="revolut-footer">
            <template v-if="step === 'selecting'">
                <Button :full-width="false" variant="secondary" @click="close">Annuler</Button>
                <Button :full-width="false" :disabled="!selectedTerminalId" @click="startPayment">Envoyer vers ce TPE</Button>
            </template>

            <template v-else-if="step === 'pending' || step === 'processing'">
                <Button :full-width="false" variant="secondary" @click="confirmCancel">Annuler</Button>
                <Button :full-width="false" @click="resend">Renvoyer au TPE</Button>
            </template>

            <template v-else-if="step === 'timeout'">
                <Button :full-width="false" variant="secondary" @click="confirmCancel">Annuler</Button>
                <Button :full-width="false" @click="refreshStatus">Rafraîchir</Button>
            </template>

            <template v-else-if="step === 'failed'">
                <Button :full-width="false" variant="secondary" @click="close">Fermer</Button>
                <Button :full-width="false" variant="secondary" @click="step = 'selecting'">Changer de TPE</Button>
                <Button :full-width="false" @click="retryOnSameTerminal">Réessayer</Button>
            </template>

            <template v-else-if="step === 'cancelled'">
                <Button :full-width="false" @click="close">Fermer</Button>
            </template>
        </footer>
    </VueFinalModal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { VueFinalModal } from 'vue-final-modal'
import { toast } from 'vue3-toastify'
import * as Sentry from '@sentry/browser'
import Button from '@/components/atoms/Button.vue'
import IconSvg from '@/components/atoms/ASvg.vue'
import { useCartStore } from '@/stores/CartStore'
import { usePaymentTerminalStore } from '@/stores/PaymentTerminalStore'
import { paymentTerminalService } from '@/services/payment-terminal.service'
import type { IPaymentTerminal, IRevolutTransaction } from '@/interfaces/IPaymentTerminal'

// ─── Constantes ───────────────────────────────────────────────────────────────
const POLL_INTERVAL_MS = 1500
const TIMEOUT_SECONDS = 120

// ─── Stores ───────────────────────────────────────────────────────────────────
const cartStore = useCartStore()
const terminalStore = usePaymentTerminalStore()

// ─── Props / Emits ────────────────────────────────────────────────────────────
const emit = defineEmits<{
    (e: 'paid'): void
    (e: 'close'): void
}>()

// ─── État local ───────────────────────────────────────────────────────────────
type Step = 'selecting' | 'pending' | 'processing' | 'failed' | 'cancelled' | 'timeout'

const isOpen = ref(true)
const step = ref<Step>(
    terminalStore.defaultTerminalId ? 'pending' : 'selecting'
)
const selectedTerminalId = ref<string | null>(terminalStore.defaultTerminalId)
const currentTransaction = ref<IRevolutTransaction | null>(null)
const errorMessage = ref<string | null>(null)
const elapsed = ref(0)

// ─── Computed ─────────────────────────────────────────────────────────────────
const formattedAmount = computed(() => {
    const amount = cartStore.totalCartWithFees.amount
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount)
})

const activeTerminal = computed((): IPaymentTerminal | null => {
    if (!selectedTerminalId.value) return null
    return terminalStore.availableTerminals.find(t => t._id === selectedTerminalId.value) ?? null
})

// ─── Polling ──────────────────────────────────────────────────────────────────
let pollInterval: ReturnType<typeof setInterval> | null = null
let elapsedInterval: ReturnType<typeof setInterval> | null = null

function startPolling() {
    stopPolling()
    elapsed.value = 0

    elapsedInterval = setInterval(() => {
        elapsed.value++
        if (elapsed.value >= TIMEOUT_SECONDS) {
            step.value = 'timeout'
            stopPolling()
        }
    }, 1000)

    pollInterval = setInterval(async () => {
        if (!currentTransaction.value) return
        try {
            const tx = await paymentTerminalService.getStatus(currentTransaction.value._id)
            currentTransaction.value = tx
            if (tx.status === 'completed') {
                stopPolling()
                // Plus de prompt 'choix recu' : on enchaine direct sur
                // l'ecran de confirmation (SummaryView).
                emit('paid')
                return
            } else if (tx.status === 'failed') {
                stopPolling()
                errorMessage.value = 'Le paiement a été refusé.'
                step.value = 'failed'
            } else if (tx.status === 'cancelled') {
                stopPolling()
                step.value = 'cancelled'
            } else if (tx.status === 'processing') {
                step.value = 'processing'
            }
        } catch (error: any) {
            // Erreurs réseau transitoires : on continue à poller
            Sentry.captureException(error)
        }
    }, POLL_INTERVAL_MS)
}

function stopPolling() {
    if (pollInterval) { clearInterval(pollInterval); pollInterval = null }
    if (elapsedInterval) { clearInterval(elapsedInterval); elapsedInterval = null }
}

// ─── Actions ──────────────────────────────────────────────────────────────────
async function startPayment() {
    if (!selectedTerminalId.value || !cartStore.order) return

    step.value = 'pending'
    errorMessage.value = null

    const amountCentimes = Math.round(cartStore.totalCartWithFees.amount * 100)

    try {
        // Pas de `lineItems` ici : l'API construit la liste depuis l'Order
        // (billets + addons + adjustments insurance/editionFees) via
        // buildLineItemsFromOrder. Source unique de vérité.
        const tx = await paymentTerminalService.initiate({
            orderId: cartStore.order._id,
            terminalId: selectedTerminalId.value,
            amount: amountCentimes,
            currency: 'EUR',
        })
        currentTransaction.value = tx
        startPolling()
    } catch (error: any) {
        Sentry.captureException(error)
        const status = error?.response?.status
        if (status === 502 || status === 504) {
            errorMessage.value = 'Revolut est indisponible. Basculez en paiement manuel.'
        } else if (status === 403) {
            errorMessage.value = 'Ce terminal n\'est pas disponible pour votre organisation.'
        } else {
            errorMessage.value = 'Impossible de démarrer le paiement.'
        }
        step.value = 'failed'
    }
}

async function confirmCancel() {
    if (!currentTransaction.value) {
        close()
        return
    }
    stopPolling()
    try {
        await paymentTerminalService.cancel(currentTransaction.value._id, 'Annulation opérateur')
    } catch (error: any) {
        Sentry.captureException(error)
    }
    step.value = 'cancelled'
}

async function resend() {
    if (!currentTransaction.value) return
    try {
        await paymentTerminalService.resend(currentTransaction.value._id)
        toast('Demande de paiement renvoyée au terminal')
    } catch (error: any) {
        Sentry.captureException(error)
        toast.error('Impossible de renvoyer la demande')
    }
}

async function refreshStatus() {
    if (!currentTransaction.value) return
    step.value = 'pending'
    startPolling()
}

async function retryOnSameTerminal() {
    currentTransaction.value = null
    errorMessage.value = null
    await startPayment()
}

function close() {
    stopPolling()
    emit('close')
}

// ─── Cycle de vie ─────────────────────────────────────────────────────────────
onMounted(async () => {
    // Si un TPE par défaut est configuré, démarrer directement
    if (terminalStore.defaultTerminalId) {
        await startPayment()
    }
})

onUnmounted(() => {
    stopPolling()
})
</script>
